import { Injectable } from '@angular/core';

export interface RegisteredGame {
  id: string;
  name: string;
  executable: string;
  defaultInstallPaths?: string[];
  customInstallPath?: string;
  customExecutable?: string;
  requiredVersion?: string;
  launchArgs?: string[];
  installed?: boolean;
  running?: boolean;
  coverUrl?: string;
  genre?: string;
  developer?: string;
  releaseYear?: number;
  sizeGb?: number;
}

declare global {
  interface Window {
    games: {
      getAllGames: () => Promise<RegisteredGame[]>;
      isInstalled: (gameId: string) => Promise<boolean>;
      isGameRunning: (gameId: string) => Promise<boolean>;
      launch: (gameId: string, extraArgs?: string[]) => Promise<boolean>;
      openWebGame?: (url: string) => void;
      browseGamePath: (gameId: string) => Promise<{ success: boolean; path?: string; executable?: string; installed?: boolean }>;
      setGamePath: (gameId: string, path: string) => Promise<{ success: boolean; path?: string; installed?: boolean }>;
      setGameExecutable: (gameId: string, executable: string) => Promise<{ success: boolean; executable?: string; installed?: boolean }>;
    };
  }
}

@Injectable({
  providedIn: 'root',
})
export class GamesService {
  
  /**
   * Get all registered games from container / service.
   */
  async getAllGames(): Promise<RegisteredGame[]> {
    if (window.games?.getAllGames) {
      return await window.games.getAllGames();
    }
    // Fallback if running outside Electron
    return [];
  }

  /**
   * Check if a game is installed.
   */
  async isGameInstalled(gameId: string): Promise<boolean> {
    if (window.games?.isInstalled) {
      return await window.games.isInstalled(gameId);
    }
    return false;
  }

  /**
   * Check if the game is running.
   */
  async isGameRunning(gameId: string): Promise<boolean> {
    if (window.games?.isGameRunning) {
      return await window.games.isGameRunning(gameId);
    }
    return false;
  }

  /**
   * Launch a game.
   */
  async launchGame(gameId: string, extraArgs: string[] = []): Promise<boolean> {
    if (window.games?.launch) {
      return await window.games.launch(gameId, extraArgs);
    }
    return false;
  }

  /**
   * Open native file dialog to browse for game installation directory or executable.
   */
  async browseGamePath(gameId: string) {
    if (window.games?.browseGamePath) {
      return await window.games.browseGamePath(gameId);
    }
    return { success: false };
  }

  /**
   * Manually set game installation path.
   */
  async setGamePath(gameId: string, path: string) {
    if (window.games?.setGamePath) {
      return await window.games.setGamePath(gameId, path);
    }
    return { success: false };
  }

  /**
   * Manually set game executable binary name.
   */
  async setGameExecutable(gameId: string, executable: string) {
    if (window.games?.setGameExecutable) {
      return await window.games.setGameExecutable(gameId, executable);
    }
    return { success: false };
  }
}
